import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, from } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

import { StockChangeRecord } from './stock-change.config';
import {
  ChangeLogControllerService,
  StockChangeListDTO,
  StockChangeVO,
  CategoryControllerService,
} from '@app/api';

/** qtyTypeCode (API) → i18n key for inventory_lookup.table.columns (bản tra cứu tồn kho) */
const QTY_TYPE_I18N_KEYS: Record<string, string> = {
  stockNum: 'inventory_lookup.table.columns.on_hand',
  usableNum: 'inventory_lookup.table.columns.available',
  reservedNum: 'inventory_lookup.table.columns.reserved',
  preemptionNum: 'inventory_lookup.table.columns.allocated',
  lockNum: 'inventory_lookup.table.columns.locked',
  wayNum: 'inventory_lookup.table.columns.in_transit',
};

export interface StockChangeSearchParams {
  spuCode?: string;
  spuName?: string;
  skuCode?: string;
  categoryId?: string;
  warehouseCode?: string;
  warehouseName?: string;
  recordTypeCodeList?: string[];
  warehouseTypeCodeList?: string[];
  beginDate?: string;
  endDate?: string;
  recordCode?: string;
  page?: number;
  size?: number;
}

export interface StockChangeResponse {
  content: StockChangeRecord[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

@Injectable()
export class StockChangeService {
  constructor(
    private changeLogService: ChangeLogControllerService,
    private categoryService: CategoryControllerService,
    private translateService: TranslateService,
  ) {}

  getChangeRecords(
    params: StockChangeSearchParams,
  ): Observable<StockChangeResponse> {
    // Convert frontend params to API DTO format
    const apiParams: StockChangeListDTO = {
      page: params.page || 0,
      size: params.size || 20,
      spuCode: params.spuCode || undefined,
      spuName: params.spuName || undefined,
      skuCode: params.skuCode || undefined,
      categoryId: params.categoryId ? parseInt(params.categoryId) : undefined,
      warehouseCode: params.warehouseCode || undefined,
      warehouseName: params.warehouseName || undefined,
      recordTypeCodeList: params.recordTypeCodeList || [],
      warehouseTypeCodeList: params.warehouseTypeCodeList || [],
      beginDate: params.beginDate || undefined,
      endDate: params.endDate || undefined,
      recordCode: params.recordCode || undefined,
    };

    return this.changeLogService.page5(apiParams).pipe(
      // API returns Blob when Accept is */* — parse to JSON first. Response shape: { page: { pageContent: [...], totalElements, size, number, totalPages } }
      switchMap((response: StockChangeVO | Blob) => {
        if (response instanceof Blob) {
          return from(this.parseBlobToJson(response));
        }
        return of(response as Record<string, unknown>);
      }),
      map((raw: Record<string, unknown>) => {
        const pageInfo = (raw['page'] ??
          (raw['data'] as Record<string, unknown>)?.['page']) as
          | Record<string, unknown>
          | undefined;
        const list: unknown[] = Array.isArray(pageInfo?.['pageContent'])
          ? pageInfo['pageContent']
          : Array.isArray(pageInfo?.['page_content'])
            ? pageInfo['page_content']
            : [];
        const stockChangeRecords = list.map((item: unknown) =>
          this.mapApiResponseToStockChangeRecord(
            item as Record<string, unknown>,
          ),
        );
        const num = (key: string) => {
          const v =
            pageInfo?.[key] ??
            pageInfo?.[key.replace(/([A-Z])/g, '_$1').toLowerCase()];
          return typeof v === 'number' ? v : 0;
        };
        return {
          content: stockChangeRecords,
          totalElements: num('totalElements'),
          totalPages: num('totalPages'),
          size: num('size') || 20,
          number: num('number'),
        };
      }),
      catchError((error) => {
        console.error('Error fetching stock change data:', error);
        return of({
          content: [],
          totalElements: 0,
          totalPages: 0,
          size: 20,
          number: 0,
        });
      }),
    );
  }

  private parseBlobToJson(blob: Blob): Promise<Record<string, unknown>> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          resolve(
            JSON.parse(reader.result as string) as Record<string, unknown>,
          );
        } catch (e) {
          reject(e);
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsText(blob);
    });
  }

  private mapApiResponseToStockChangeRecord(
    apiItem: Record<string, unknown>,
  ): StockChangeRecord {
    const toSnake = (s: string) =>
      s.replace(/[A-Z]/g, (c) => '_' + c.toLowerCase());
    const get = (key: string) => apiItem[key] ?? apiItem[toSnake(key)];
    const str = (key: string) => String(get(key) ?? '').trim();
    const num = (key: string) => {
      const v = get(key);
      return typeof v === 'number' && !Number.isNaN(v)
        ? v
        : v != null
          ? Number(v) || 0
          : 0;
    };
    const spuCode = str('spuCode');
    const skuCode = str('skuCode');
    const recordCode = str('recordCode');
    const recordTypeCode = str('recordTypeCode');
    const warehouseTypeCode = str('warehouseTypeCode');
    const qtyTypeCode = str('qtyTypeCode');
    return {
      id: `${spuCode || '-'}-${skuCode || '-'}-${recordCode || '-'}`,
      spuCode,
      spuName: str('spuName'),
      skuCode,
      categoryName: str('categoryName'),
      warehouseCode: str('warehouseCode'),
      warehouseName: str('warehouseName'),
      recordTypeCode,
      recordTypeMeaning:
        this.mapRecordTypeCodeToLabel(recordTypeCode) ||
        str('recordTypeMeaning'),
      warehouseTypeCode,
      warehouseTypeMeaning:
        this.mapWarehouseTypeCodeToLabel(warehouseTypeCode) ||
        str('warehouseTypeMeaning'),
      qtyTypeCode,
      qtyTypeMeaning:
        this.mapQtyTypeCodeToLabel(qtyTypeCode) || str('qtyTypeMeaning'),
      preRealNum: num('preRealNum'),
      changeNum: num('changeNum'),
      aftRealNum: num('aftRealNum'),
      createdBy: num('createdBy'),
      creationDate: str('creationDate'),
      createdName: str('createdName'),
      recordCode,
      orderCode: str('orderCode'),
      reasonCode: str('reasonCode'),
      reasonMeaning: this.mapReasonCodeToLabel(str('reasonCode')),
      actor: str('actor'),
    };
  }

  private mapReasonCodeToLabel(code: string): string {
    if (!code) return '';
    const key = `inventory_change_lookup.table.release_reasons.${code}`;
    const out = this.translateService.instant(key);
    return out !== key ? out : code;
  }

  private mapRecordTypeCodeToLabel(code: string): string {
    if (!code) return '';
    const key = `inventory_change_lookup.table.record_types.${code}`;
    const out = this.translateService.instant(key);
    return out !== key ? out : '';
  }

  private mapWarehouseTypeCodeToLabel(code: string): string {
    if (!code) return '';
    const key = `inventory_change_lookup.table.warehouse_types.${code}`;
    const out = this.translateService.instant(key);
    return out !== key ? out : '';
  }

  private mapQtyTypeCodeToLabel(code: string): string {
    if (!code) return '';
    const i18nKey = QTY_TYPE_I18N_KEYS[code];
    if (!i18nKey) return '';
    return this.translateService.instant(i18nKey);
  }

  getCategoryOptions(params?: any): Observable<any[]> {
    const page = params?.page ? params?.page - 1 : undefined;
    return this.categoryService
      .getList2(
        undefined,
        undefined,
        undefined,
        undefined,
        params?.statusFlag,
        undefined,
        undefined,
        undefined,
        page,
        params?.size,
      )
      .pipe(
        switchMap((response: any) => {
          if (response instanceof Blob) {
            return from(this.parseBlobToJson(response)).pipe(
              map((json) => this.mapCategoryListToOptions(json)),
            );
          }
          return of(this.mapCategoryListToOptions(response));
        }),
        catchError((error) => {
          console.error('Error fetching category tree:', error);
          return of([]);
        }),
      );
  }

  private mapCategoryListToOptions(response: any): any[] {
    if (!response) {
      return [];
    }

    const content = response.content || response.pageContent || response.data;

    if (!content || !Array.isArray(content)) {
      return [];
    }

    return content.map((item: any) => ({
      label: item.name || '',
      value: item.id?.toString() || '',
      // Note: The API returns a flat list, so we need to build the tree structure
      // For now, we'll return a flat structure since the API doesn't include children
    }));
  }
}
